import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { RootState } from 'store';

import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import CustomButton from 'components/Button';
import { JOB_MODAL, projectModalActions, TASK_MODAL } from 'features/Project/projectModalSlice';
import { useLazyGetJobByIdQuery, useUpdateJobTaskOrderMutation } from 'services/busmanApi';
import { JobRequest, TaskMinList, TaskRequest, UpdateTaskOrderPayload } from 'types/JobTypes';
import { ProjectManagementJobResponse } from 'types/ProjectTypes';
import { TASK_STATUS } from 'utils/constants';
import { getStatusName } from 'utils/string';

import { StatusPill, TaskListHeader } from './LineItemComponents';
interface JobLineItemProps {
  jobItem: ProjectManagementJobResponse;
}

const IconStyles = { fontSize: 32, padding: '4px', borderRadius: 2, color: '#463947', border: '1px solid #E7E4E7' };

interface SortableTaskRowProps {
  task: TaskMinList;
  isReordering: boolean;
  getAssigneeName: (assigneeId: string | null) => string;
  onEdit: (event: React.MouseEvent, task: TaskMinList) => void;
}

const SortableTaskRow = ({ task, isReordering, getAssigneeName, onEdit }: SortableTaskRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
    disabled: !isReordering,
  });

  const draggableStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <Tooltip title={task.description}>
      <Grid
        ref={setNodeRef}
        style={draggableStyle}
        container
        spacing={1}
        sx={{
          my: 1,
          padding: '4px',
          '&:hover': {
            backgroundColor: 'action.hover',
            cursor: 'pointer',
          },
        }}>
        {[
          { value: task.name, breakpoint: 2 },
          { value: task.due_date, breakpoint: 0 },
          { value: task.percent_complete, breakpoint: 0 },
          { value: getAssigneeName(task.assigned_to_id), breakpoint: 0 },
        ].map((cell, i) => (
          <Grid key={i} item {...(cell.breakpoint ? { xs: cell.breakpoint } : { xs: true })}>
            <Typography variant="subtitle1">{cell.value}</Typography>
          </Grid>
        ))}
        <Grid item xs>
          {StatusPill(task.status)}
        </Grid>
        <Grid item xs>
          <Typography variant="subtitle1">{task.priority}</Typography>
        </Grid>

        <Grid item xs={1}>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, justifyContent: 'flex-start' }}>
            {isReordering ? (
              <Tooltip title="Drag to reorder">
                <DragIndicatorIcon
                  sx={{ ...IconStyles, cursor: 'grab', touchAction: 'none' }}
                  {...attributes}
                  {...listeners}
                />
              </Tooltip>
            ) : (
              <>
                <EditIcon sx={IconStyles} onClick={(e) => onEdit(e, task)} />
                <ChevronRightIcon sx={IconStyles} />
              </>
            )}
          </Box>
        </Grid>
      </Grid>
    </Tooltip>
  );
};

/**
 * @returns Job Line Item Component
 */
const JobLineItem = ({ jobItem }: JobLineItemProps) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [fetchJobDetail] = useLazyGetJobByIdQuery();
  const [updateJobTaskOrder, { isLoading: isSavingOrder }] = useUpdateJobTaskOrderMutation();
  const { assigneeList, assignee } = useSelector((state: RootState) => state.projectFilterReducer);

  // Local ordering kept in sync with the incoming task list, sorted by task_order.
  // While in reorder mode, drag-and-drop mutates this list locally; changes are
  // only persisted when the user exits reorder mode.
  const [orderedTasks, setOrderedTasks] = React.useState<TaskMinList[]>([]);
  const [isReordering, setIsReordering] = React.useState(false);

  React.useEffect(() => {
    setOrderedTasks([...jobItem.task_list].sort((a, b) => a.task_order - b.task_order));
  }, [jobItem.task_list]);

  // Reordering a filtered subset would scramble the global order, so only allow it
  // when the full task list is visible.
  const canReorder = assignee === 'All';

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Handle filtering
  const filteredTasks = React.useMemo(() => {
    return orderedTasks.filter((task) => {
      const matchesAssignee = assignee === 'All' || task.assigned_to_id === assignee;

      return matchesAssignee;
    });
  }, [orderedTasks, assignee]);

  // Reorder locally only; changes are persisted on exiting reorder mode.
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setOrderedTasks((prev) => {
      const oldIndex = prev.findIndex((task) => task.id === active.id);
      const newIndex = prev.findIndex((task) => task.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;

      return arrayMove(prev, oldIndex, newIndex).map((task, index) => ({ ...task, task_order: index }));
    });
  };

  const handleToggleReorder = async () => {
    // Entering reorder mode — just switch on the drag handles.
    if (!isReordering) {
      setIsReordering(true);
      return;
    }

    // Exiting reorder mode — persist the full ordering as a single request if it
    // changed from what is currently saved on the server.
    const persistedOrderById = new Map(jobItem.task_list.map((task) => [task.id, task.task_order]));
    const hasChanges = orderedTasks.some((task) => persistedOrderById.get(task.id) !== task.task_order);

    if (hasChanges) {
      const payload: UpdateTaskOrderPayload = {
        tasks: orderedTasks.map((task, index) => ({
          task_id: task.id,
          task_order: index + 1,
        })),
      };

      const response = await updateJobTaskOrder(payload);

      // Roll back to the persisted order if the update failed.
      if ('error' in response) {
        setOrderedTasks([...jobItem.task_list].sort((a, b) => a.task_order - b.task_order));
      }
    }

    setIsReordering(false);
  };

  const editJobHandler = async (event: React.MouseEvent) => {
    event.stopPropagation();
    const detail = await fetchJobDetail(jobItem.job_id);
    const job = detail.data;

    if (!job) return;

    const jobRequest: JobRequest = {
      job_id: job.job_id,
      client_id: job.client.client_id,
      job_manager: job.job_manager.user_id,
      teams: null,
      activities: job.activities.map((activity) => ({
        activityId: activity.activity_id,
        activityName: activity.activity_name,
      })),
      invoice_header: job.invoice_header,
      job_description: job.job_description,
      job_status: job.job_status,
      job_name: job.job_number,
      job_type: job.job_type,
      is_active: job.is_active,
      start_date: job.start_date,
      end_date: job.end_date,
      introduced_in_scope_id: job.introduced_in_scope?.id ?? '',
      removed_in_scope: job.removed_in_scope ?? '',
      percent_complete: Number(job.percent_complete),
      priority: job.priority,
      job_billing_cycle: job.job_billing_cycle,
    };

    dispatch(
      projectModalActions.openEditModal({
        type: JOB_MODAL,
        data: jobRequest,
      }),
    );
  };

  const editTaskHandler = (event: React.MouseEvent, taskListItem: TaskMinList) => {
    event.stopPropagation();

    const editTask: TaskRequest = {
      ...taskListItem,
      id: taskListItem.id,
      job_id: jobItem.job_id,
      introduced_in_scope_id: jobItem.introduced_in_scope_id,
      removed_in_scope: null,
      assigned_to_id: taskListItem.assigned_to_id ?? '',
      estimated_hours: Number(taskListItem.estimated_hours) ?? 0,
      actual_hours: Number(taskListItem.actual_hours) ?? 0,
    };

    dispatch(
      projectModalActions.openEditModal({
        type: TASK_MODAL,
        data: editTask,
      }),
    );
  };

  const getAssigneeName = (assigneeId: string | null) => {
    if (assigneeId === null) return 'Not assigned';
    return assigneeList.find((item) => item.user_id === assigneeId)?.full_name ?? 'Not assigned';
  };

  return (
    <>
      <Accordion disableGutters>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ fontSize: 32 }} />}
          sx={{
            flexDirection: 'row-reverse',
            '& .MuiAccordionSummary-expandIconWrapper': {
              transform: 'rotate(270deg)',
              color: theme.palette.busmanColors.lightGrey, // example
            },
            '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
              transform: 'rotate(0deg)',
            },
            '& .MuiAccordionSummary-content': {
              marginLeft: 1,
            },
            padding: '2px',
            borderRadius: 1,
            backgroundColor: '#E7E4E7',
          }}>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
            <Stack gap={1}>
              <Typography variant="h4">
                {jobItem.job_name} - {getStatusName(TASK_STATUS, jobItem.job_status)}
              </Typography>
              <Typography variant="h4">
                {filteredTasks.length > 0 ? `${filteredTasks.length} tasks` : 'No tasks assigned'}
              </Typography>
            </Stack>

            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
              {jobItem.task_list.length > 0 && (
                <CustomButton
                  variant={isReordering ? 'contained' : 'outlined'}
                  text={isReordering ? 'Save order' : 'Reorder'}
                  width="medium"
                  disabled={isSavingOrder || (!isReordering && !canReorder)}
                  toolTip={!canReorder ? 'Clear the assignee filter to reorder tasks' : ''}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleToggleReorder();
                  }}
                />
              )}
              <Tooltip title="Add new task to the given job number">
                <AddIcon
                  sx={IconStyles}
                  onClick={(event) => {
                    event.stopPropagation();
                    dispatch(
                      projectModalActions.openAddModal({
                        type: TASK_MODAL,
                        data: {
                          job_id: jobItem.job_id,
                          introduced_in_scope_id: jobItem.introduced_in_scope_id,
                        },
                      }),
                    );
                  }}
                />
              </Tooltip>
              <Tooltip title="Update the current job details">
                <EditIcon sx={IconStyles} onClick={editJobHandler} />
              </Tooltip>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {jobItem.task_list.length > 0 ? (
            <>
              <TaskListHeader />
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                onDragEnd={handleDragEnd}>
                <SortableContext items={filteredTasks.map((task) => task.id)} strategy={verticalListSortingStrategy}>
                  {filteredTasks.map((task) => (
                    <SortableTaskRow
                      key={task.id}
                      task={task}
                      isReordering={isReordering}
                      getAssigneeName={getAssigneeName}
                      onEdit={editTaskHandler}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </>
          ) : (
            <CustomButton
              variant="outlined"
              text="Add a new task"
              width="100%"
              height="208px"
              endIcon={
                <AddIcon sx={{ color: theme.palette.busmanColors.lightGrey, width: '15px', fontWeight: 'bold' }} />
              }
              customSx={{
                '&:focus': {
                  borderColor: '#9531FF',
                  outline: 'none',
                  boxShadow: 'none',
                },
              }}
              onClick={(event) => {
                event.stopPropagation();
                dispatch(
                  projectModalActions.openAddModal({
                    type: TASK_MODAL,
                    data: { job_id: jobItem.job_id, introduced_in_scope_id: jobItem.introduced_in_scope_id },
                  }),
                );
              }}
            />
          )}
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default JobLineItem;
